import {
  buyerAnnualCapExAtom,
  buyerCompTotalAtom,
  buyerDeprAndAmortScheduleAtom,
  buyerDeprAndAmortScheduleDealExpensesAtom,
  companysTaxRateAtom,
  equityInvestorDollarAtom,
  equityInvestorPreferredEquityRateAtom,
  equitySearchPhaseCapitalDollarAtom,
  equitySellerRolloverDollarAtom,
  includeInvestorPayoutInHistoricalAtom,
  includeSellerNoteInHistoricalAtom,
  isEquityCalculationMethodUsingProjectCostAtom,
  isExternalEquityAtom,
  targetPurchasePriceAtom,
  totalDealExpensesAtom,
} from "./general";

import {
  epochMonthCount,
  indexOfLastFullYearEpochAtom,
  loanEpochsAtomsContainer,
} from "@/financeModels/owasco/epoch";
import {
  isNumber,
  evaluteWithErrors as solve,
  toNumberOrNull,
} from "@/utils/math";
import { getMoleculeValueAtIndex } from "@/utils/molecule";
import { Atom, Getter, atom } from "jotai";
import { DollarAmount, MathResult, PercentAmount } from "../types";
import {
  AmortizationPayment,
  sbaLoanAmortizationPaymentsAtom,
  sellersNoteAmortizationPaymentsAtom,
} from "./amortization";
import {
  acquisitionInvestorsPercentCommonEquityEquityMethodAtom,
  acquisitionInvestorsPercentCommonEquityProjectCostMethodAtom,
  searchPhaseInvestorsPercentCommonEquityEquityMethodAtom,
  searchPhaseInvestorsPercentCommonEquityProjectCostMethodAtom,
  searcherCoInvestPercentCommonEquityEquityMethodAtom,
  searcherCoInvestPercentCommonEquityProjectCostMethodAtom,
  sellerRolloverPercentCommonEquityEquityMethodAtom,
  sellerRolloverPercentCommonEquityProjectCostMethodAtom,
  totalFinanceCapitalInvestedAtom,
} from "./companyOwnership";
import {
  createComputedCellMoleculeFromEpochs,
  createStorableDollarMoleculeFromEpochs,
} from "./epochMolecule";
import {
  createAggregatedDollarItemizationAtom,
  getSummaryValueAtIndex,
} from "./itemization";
const { orderedEpochsMolecule } = loanEpochsAtomsContainer;

////
// Company Financials
////
export const grossRevenueItemization = createAggregatedDollarItemizationAtom(
  "lookback|grossRevenue",
);

export const grossRevenuePercentGrowthMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    // get epoch type
    const epochType = get(get(orderedEpochsMolecule)[i]).type;

    if (epochType === "partial")
      return {
        type: "text",
        isLocked: true,
        value: "-",
      };

    return {
      type: "percent",
      isLocked: true,
      value: solve<DollarAmount>("currentYear/previousYear - 1", {
        currentYear: getSummaryValueAtIndex(grossRevenueItemization, i, get),
        previousYear:
          i == 0
            ? null
            : getSummaryValueAtIndex(grossRevenueItemization, i - 1, get),
      }),
    };
  });

export const costOfGoodsSoldItemization = createAggregatedDollarItemizationAtom(
  "lookback|costOfGoodsSold",
  { isNegative: true },
);

export const grossProfitMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => ({
    type: "dollar",
    isLocked: true,
    value: solve<DollarAmount>("grossRevenue - costOfGoodsSold", {
      grossRevenue: getSummaryValueAtIndex(grossRevenueItemization, i, get),
      costOfGoodsSold:
        getSummaryValueAtIndex(costOfGoodsSoldItemization, i, get) || 0,
    }),
  }),
);

export const grossProfitPercentGrowthMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const epochType = get(get(orderedEpochsMolecule)[i]).type;

    if (epochType === "partial")
      return {
        type: "text",
        isLocked: true,
        value: "-",
      };

    return {
      type: "percent",
      isLocked: true,
      value: solve<DollarAmount>("currentYear/previousYear - 1", {
        currentYear: get(get(grossProfitMolecule)[i]).value,
        previousYear:
          i == 0 ? null : get(get(grossProfitMolecule)[i - 1]).value,
      }),
    };
  });

export const grossMarginsMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => ({
    type: "percent",
    isLocked: true,
    value: solve<PercentAmount>("grossProfit / grossRevenue", {
      grossProfit: get(get(grossProfitMolecule)[i]).value,
      grossRevenue: getSummaryValueAtIndex(grossRevenueItemization, i, get),
    }),
  }),
);

export const operatingExpensesItemization =
  createAggregatedDollarItemizationAtom("lookbackOperatingExpenses", {
    isNegative: true,
  });

export const otherIncomeMolecule = createStorableDollarMoleculeFromEpochs(
  "lookbackOtherIncome",
);

export const netIncomeMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => {
    return {
      type: "dollar",
      isLocked: true,
      value: solve<DollarAmount>(
        "grossProfit - operatingExpenses + otherIncome",
        {
          grossProfit: get(get(grossProfitMolecule)[i]).value,
          operatingExpenses:
            getSummaryValueAtIndex(operatingExpensesItemization, i, get) || 0,
          otherIncome: get(get(otherIncomeMolecule)[i]).value || 0,
        },
      ),
    };
  },
);

export const netIncomeGrowthMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => {
    const epochType = get(get(orderedEpochsMolecule)[i]).type;

    if (epochType === "partial")
      return {
        type: "text",
        isLocked: true,
        value: "-",
      };

    return {
      type: "percent",
      isLocked: true,
      value: solve<DollarAmount>("currentYear/previousYear - 1", {
        currentYear: get(get(netIncomeMolecule)[i]).value,
        previousYear: i == 0 ? null : get(get(netIncomeMolecule)[i - 1]).value,
      }),
    };
  },
);

////
// Seller's Financial Adjustments
////

export const sellersInterestExpenseMolecule =
  createStorableDollarMoleculeFromEpochs("lookbackSellersInterestExpense");

export const sellersDeprAmortMolecule = createStorableDollarMoleculeFromEpochs(
  "lookbackSellersDeprAmort",
);

export const sellersIncomeTaxMolecule = createStorableDollarMoleculeFromEpochs(
  "lookbackSellersIncomeTax",
);

export const sellersEBITDAMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => ({
    type: "dollar",
    isLocked: true,
    value: solve<DollarAmount>(
      "netIncome + sellersInterestExpense + sellersDeprAmort + sellersIncomeTax",
      {
        netIncome: get(get(netIncomeMolecule)[i]).value,
        sellersInterestExpense:
          get(get(sellersInterestExpenseMolecule)[i]).value || 0,
        sellersDeprAmort: get(get(sellersDeprAmortMolecule)[i]).value || 0,
        sellersIncomeTax: get(get(sellersIncomeTaxMolecule)[i]).value || 0,
      },
    ),
  }),
);

export const sellersEBITDAMarginsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "percent",
    isLocked: true,
    value: solve<PercentAmount>("sellersEBITDA / grossRevenue", {
      sellersEBITDA: get(get(sellersEBITDAMolecule)[i]).value,
      grossRevenue: getSummaryValueAtIndex(grossRevenueItemization, i, get),
    }),
  }));

export const addbacksAndAdjustmentsItemization =
  createAggregatedDollarItemizationAtom("lookbackAddbacksAndAdjustments");

export const sellersDiscretionaryEarningsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    value: solve<DollarAmount>("sellersEBITDA + addbacksAndAdjustments", {
      sellersEBITDA: get(get(sellersEBITDAMolecule)[i]).value,
      addbacksAndAdjustments:
        getSummaryValueAtIndex(addbacksAndAdjustmentsItemization, i, get) || 0,
    }),
  }));

export const sellersDiscretionaryEarningsGrowthMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const epochType = get(get(orderedEpochsMolecule)[i]).type;

    if (epochType === "partial")
      return {
        type: "text",
        isLocked: true,
        value: "-",
      };

    return {
      type: "percent",
      isLocked: true,
      value: solve<DollarAmount>("currentYear/previousYear - 1", {
        currentYear: get(get(sellersDiscretionaryEarningsMolecule)[i]).value,
        previousYear:
          i == 0
            ? null
            : get(get(sellersDiscretionaryEarningsMolecule)[i - 1]).value,
      }),
    };
  });

export const sellersDiscretionaryEarningsMarginsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    return {
      type: "percent",
      isLocked: true,
      value: solve<PercentAmount>(
        "sellersDiscretionaryEarnings / grossRevenue",
        {
          sellersDiscretionaryEarnings: get(
            get(sellersDiscretionaryEarningsMolecule)[i],
          ).value,
          grossRevenue: getSummaryValueAtIndex(grossRevenueItemization, i, get),
        },
      ),
    };
  });

////
// Buyer's Pro Forma Adjustments
////
const getPartialYearValue = (get: Getter, i: number, fullYearValue: number) => {
  const epoch = get(get(orderedEpochsMolecule)[i]);
  const months = epochMonthCount(epoch);
  if (epoch.type === "full") return fullYearValue;
  return (fullYearValue / 12) * months;
};

export const buyersCompensationMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => {
    return {
      type: "dollar",
      isLocked: true,
      isNegative: true,
      value: getPartialYearValue(get, i, Number(get(buyerCompTotalAtom))),
    };
  },
);

export const newOperatingExpensesItemization =
  createAggregatedDollarItemizationAtom("lookbackNewOperatingExpenses", {
    isNegative: true,
  });

export const buyersAdjustedEBITDAMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const sellersDiscretionaryEarnings = get(
      get(sellersDiscretionaryEarningsMolecule)[i],
    ).value;
    const buyersCompensation = get(get(buyersCompensationMolecule)[i]).value;
    const newOperatingExpenses =
      getSummaryValueAtIndex(newOperatingExpensesItemization, i, get) || 0;

    return {
      type: "dollar",
      isLocked: true,
      value: solve<DollarAmount>(
        "sellersDiscretionaryEarnings - buyersCompensation - newOperatingExpenses",
        {
          sellersDiscretionaryEarnings,
          buyersCompensation,
          newOperatingExpenses,
        },
      ),
    };
  });

export const buyersAdjustedEBITDAMarginsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "percent",
    isLocked: true,
    value: solve<PercentAmount>("buyersAdjustedEBITDA / grossRevenue", {
      buyersAdjustedEBITDA: get(get(buyersAdjustedEBITDAMolecule)[i]).value,
      grossRevenue: getSummaryValueAtIndex(grossRevenueItemization, i, get),
    }),
  }));

export const buyersPurchaseMultipleMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const epochType = get(get(orderedEpochsMolecule)[i]).type;

    if (epochType === "partial")
      return {
        type: "text",
        isLocked: true,
        value: "-",
      };

    return {
      type: "multiple",
      isLocked: true,
      value: solve<PercentAmount>(
        "targetPurchasePrice / buyersAdjustedEBITDA",
        {
          targetPurchasePrice: get(targetPurchasePriceAtom),
          buyersAdjustedEBITDA: get(get(buyersAdjustedEBITDAMolecule)[i]).value,
        },
      ),
    };
  });

////
// Amortization Expense Calculations
////
const calcDebtPartialYearAmortization = (
  get: Getter,
  index: number,
  paymentsAtom: Atom<AmortizationPayment[]>,
  isActive: Atom<boolean> = atom(true),
) => {
  const months = epochMonthCount(get(get(orderedEpochsMolecule)[index]));
  const payments = get(paymentsAtom).filter((payment) => payment.principal > 0);

  let interest = 0;
  let principal = 0;
  let total = 0;

  // only first 12 payments
  for (let i = 0; i < months; i++) {
    const payment = payments[i];
    interest += payment?.interest || 0;
    principal += payment?.principal || 0;
    total += (payment?.interest || 0) + (payment?.principal || 0);
  }

  if (!get(isActive)) {
    interest = 0;
    principal = 0;
    total = 0;
  }

  return {
    interest,
    principal,
    total,
  };
};

export const sba7aLoanExpenseInterestMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: calcDebtPartialYearAmortization(
      get,
      i,
      sbaLoanAmortizationPaymentsAtom,
    ).interest,
  }));

export const sellersNoteExpenseInterestMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: calcDebtPartialYearAmortization(
      get,
      i,
      sellersNoteAmortizationPaymentsAtom,
      includeSellerNoteInHistoricalAtom,
    ).interest,
  }));

export const sba7aLoanExpensePricipalMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: calcDebtPartialYearAmortization(
      get,
      i,
      sbaLoanAmortizationPaymentsAtom,
    ).principal,
  }));

export const sellersNoteExpensePricipalMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    return {
      type: "dollar",
      isLocked: true,
      isNegative: true,
      value: calcDebtPartialYearAmortization(
        get,
        i,
        sellersNoteAmortizationPaymentsAtom,
        includeSellerNoteInHistoricalAtom,
      ).principal,
    };
  });

export const lineOfCreditPricipalMolecule =
  createComputedCellMoleculeFromEpochs(() => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: 0,
  }));

export const sba7aLoanExpenseTotalMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: calcDebtPartialYearAmortization(
      get,
      i,
      sbaLoanAmortizationPaymentsAtom,
    ).total,
  }));

export const sellersNoteExpenseTotalMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: calcDebtPartialYearAmortization(
      get,
      i,
      sellersNoteAmortizationPaymentsAtom,
      includeSellerNoteInHistoricalAtom,
    ).total,
  }));

////
// Buyer's Pro Forma Financials
////

export const annualDeprAmortizationExpensesAtom = atom<DollarAmount>((get) =>
  solve<DollarAmount>("purchasePrice / deprAndAmortSchedule", {
    purchasePrice: get(targetPurchasePriceAtom) || 0,
    deprAndAmortSchedule: get(buyerDeprAndAmortScheduleAtom),
  }),
);

export const annualDeprAmortizationExpensesMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: getPartialYearValue(
      get,
      i,
      Number(get(annualDeprAmortizationExpensesAtom)),
    ),
  }));

export const amortOfDealExpensesAtom = atom<DollarAmount>((get) =>
  solve<DollarAmount>("dealExpenses / years", {
    dealExpenses: get(totalDealExpensesAtom),
    years: get(buyerDeprAndAmortScheduleDealExpensesAtom) || 15,
  }),
);

export const amortOfDealExpensesMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => {
    const amortOfDealExpenses = Number(get(amortOfDealExpensesAtom));
    const partialYearValue = getPartialYearValue(get, i, amortOfDealExpenses);

    return {
      type: "dollar",
      isLocked: true,
      isNegative: true,
      value: partialYearValue,
    };
  },
);

export const taxableIncomeMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => {
    const buyersAdjustedEBITDA = get(
      get(buyersAdjustedEBITDAMolecule)[i],
    ).value;
    const interestExpenseSeniorTermLoan = get(
      get(sba7aLoanExpenseInterestMolecule)[i],
    ).value;
    const interestExpenseSellersNote = get(
      get(sellersNoteExpenseInterestMolecule)[i],
    ).value;

    const annualDeprAmortizationExpenses = get(
      get(annualDeprAmortizationExpensesMolecule)[i],
    ).value;

    const amortOfDealExpenses = get(get(amortOfDealExpensesMolecule)[i]).value;

    return {
      type: "dollar",
      isLocked: true,
      value: solve<DollarAmount>(
        "max(0, buyersAdjustedEBITDA - interestExpenseSeniorTermLoan - interestExpenseSellersNote - annualDeprAmortizationExpenses - amortOfDealExpenses)",
        {
          buyersAdjustedEBITDA,
          interestExpenseSeniorTermLoan,
          interestExpenseSellersNote,
          annualDeprAmortizationExpenses,
          amortOfDealExpenses,
        },
      ),
    };
  },
);

export const companysTaxRateMolecule = createComputedCellMoleculeFromEpochs(
  (get) => ({
    type: "percent",
    isLocked: true,
    value: get(companysTaxRateAtom),
  }),
);

export const incomeTaxesMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: solve<DollarAmount>(
      "bankersRound(max( 0, taxableIncome * buyerTaxRate))",
      {
        taxableIncome: get(get(taxableIncomeMolecule)[i]).value,
        buyerTaxRate: get(companysTaxRateAtom),
      },
    ),
  }),
);

export const annualMaintenanceCapExMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: getPartialYearValue(get, i, Number(get(buyerAnnualCapExAtom))),
  }));

export const unleveredFreeCashFlowMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => ({
    type: "dollar",
    isLocked: true,
    value: solve<DollarAmount>(
      "bankersRound(buyersAdjustedEBITDA - incomeTaxes - annualMaintenanceCapEx)",
      {
        buyersAdjustedEBITDA: get(get(buyersAdjustedEBITDAMolecule)[i]).value,
        incomeTaxes: get(get(incomeTaxesMolecule)[i]).value,
        annualMaintenanceCapEx: getPartialYearValue(
          get,
          i,
          Number(get(buyerAnnualCapExAtom)),
        ),
      },
    ),
  }));

export const totalDebtServiceMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => ({
    type: "dollar",
    isLocked: true,
    isNegative: true,
    value: solve<DollarAmount>(
      "sba7aLoanExpenseTotal + sellersNoteExpenseTotal",
      {
        sba7aLoanExpenseTotal:
          toNumberOrNull(
            getMoleculeValueAtIndex(get, sba7aLoanExpenseTotalMolecule, i, 0),
          ) || 0,
        sellersNoteExpenseTotal:
          toNumberOrNull(
            getMoleculeValueAtIndex(get, sellersNoteExpenseTotalMolecule, i, 0),
          ) || 0,
      },
    ),
  }),
);

export const distributableFreeCashFlowMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const unleveredFreeCashFlow = get(
      get(unleveredFreeCashFlowMolecule)[i],
    ).value;
    const totalDebtService = get(get(totalDebtServiceMolecule)[i]).value;

    return {
      type: "dollar",
      isLocked: true,
      value: solve<DollarAmount>(
        "bankersRound(unleveredFreeCashFlow - totalDebtService)",
        {
          unleveredFreeCashFlow,
          totalDebtService,
        },
      ),
    };
  });

export const dscrMolecule = createComputedCellMoleculeFromEpochs((get, i) => ({
  type: "dollar",
  isLocked: true,
  value: solve<DollarAmount>("abs(unleveredFreeCashFlow / totalDebtPayment)", {
    unleveredFreeCashFlow: get(get(unleveredFreeCashFlowMolecule)[i]).value,
    totalDebtPayment: get(get(totalDebtServiceMolecule)[i]).value,
  }),
}));

export const totalEquityPreferredDividendsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const monthCount = epochMonthCount(get(get(orderedEpochsMolecule)[i]));
    const indexOfLastFullYearEpoch = get(indexOfLastFullYearEpochAtom);

    const includeInvestorPayoutInHistorical = get(
      includeInvestorPayoutInHistoricalAtom,
    );
    const isEquityInvestorsIncluded = get(isExternalEquityAtom);

    const distributableFreeCashFlow = get(
      get(distributableFreeCashFlowMolecule)[
        monthCount < 12 ? indexOfLastFullYearEpoch : i
      ],
    ).value as MathResult;

    if (
      !toNumberOrNull(distributableFreeCashFlow) ||
      (isNumber(distributableFreeCashFlow) && distributableFreeCashFlow <= 0)
    ) {
      return {
        type: "dollar",
        isLocked: true,
        isNegative: true,
        value: 0,
      };
    }

    const totalEquity = get(totalFinanceCapitalInvestedAtom);
    const preferredEquityRate = get(equityInvestorPreferredEquityRateAtom);

    const preferredDividends = solve<DollarAmount>(
      "min(totalEquity * preferredEquityRate, distributableFreeCashFlow)",
      {
        totalEquity: totalEquity,
        preferredEquityRate: preferredEquityRate,
        distributableFreeCashFlow: distributableFreeCashFlow,
      },
    );

    if (
      includeInvestorPayoutInHistorical === false ||
      isEquityInvestorsIncluded === false
    ) {
      return {
        type: "dollar",
        isLocked: true,
        isNegative: true,
        value: 0,
      };
    }

    return {
      type: "dollar",
      isLocked: true,
      isNegative: true,
      value: getPartialYearValue(get, i, Number(preferredDividends)),
    };
  });

export const totalEquityPrincipalRepaymentsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const F149 = get(get(distributableFreeCashFlowMolecule)[i])
      .value as MathResult;
    const F154 = get(get(totalEquityPreferredDividendsMolecule)[i])
      .value as MathResult;

    const assumptionsD66 = Number(get(totalFinanceCapitalInvestedAtom)) || 0;
    const assumptionsD43 = get(isExternalEquityAtom);
    const includeInvestorPayoutInHistorical = get(
      includeInvestorPayoutInHistoricalAtom,
    );

    const sum = Number(F149) - Number(F154);
    let value = Math.min(sum, assumptionsD66);
    if (includeInvestorPayoutInHistorical === false) {
      value = 0;
    }

    if (assumptionsD43 === false || assumptionsD66 <= 0) {
      value = 0;
    }

    if (sum <= 0) {
      value = 0;
    }

    return {
      type: "dollar",
      isLocked: true,
      isNegative: true,
      value,
    };
  });

export const totalEquityProfitDistributionsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const includeInvestorPayoutInHistorical = get(
      includeInvestorPayoutInHistoricalAtom,
    );

    const assumptionsD43 = get(isExternalEquityAtom);
    const E154 = Number(
      get(get(totalEquityPreferredDividendsMolecule)[i]).value as MathResult,
    );
    const E155 = -Number(
      get(get(totalEquityPrincipalRepaymentsMolecule)[i]).value as MathResult,
    );

    const E149 = Number(
      get(get(distributableFreeCashFlowMolecule)[i]).value as MathResult,
    );

    const isUsingProjectCostMethod = get(
      isEquityCalculationMethodUsingProjectCostAtom,
    );

    const assumptionsE80 = get(
      searchPhaseInvestorsPercentCommonEquityEquityMethodAtom,
    );
    const assumptionsE81 = get(
      acquisitionInvestorsPercentCommonEquityEquityMethodAtom,
    );
    const assumptionsE82 = get(
      searcherCoInvestPercentCommonEquityEquityMethodAtom,
    );
    const assumptionsE83 = get(
      sellerRolloverPercentCommonEquityEquityMethodAtom,
    );

    const assumptionsE101 = get(
      searchPhaseInvestorsPercentCommonEquityProjectCostMethodAtom,
    );
    const assumptionsE102 = get(
      acquisitionInvestorsPercentCommonEquityProjectCostMethodAtom,
    );
    const assumptionsE103 = get(
      searcherCoInvestPercentCommonEquityProjectCostMethodAtom,
    );
    const assumptionsE104 = get(
      sellerRolloverPercentCommonEquityProjectCostMethodAtom,
    );

    const assumptionsE30 = Number(get(equityInvestorDollarAtom) || 0); // aqcuisition equity
    const assumptionsE31 = Number(get(equitySellerRolloverDollarAtom) || 0); // rollover equity
    const assumptionsD46 = Number(get(equitySearchPhaseCapitalDollarAtom) || 0);

    let value = 0;

    const sum = Number(E154) + Number(E155);
    if (Math.abs(sum) <= E149) {
      const multiplier =
        isUsingProjectCostMethod === false
          ? assumptionsE80 + assumptionsE81 + assumptionsE82 + assumptionsE83
          : assumptionsE101 +
            assumptionsE102 +
            assumptionsE103 +
            assumptionsE104;

      value = (E154 - E149 - E155) * multiplier;
    }

    if (
      assumptionsD43 === false ||
      assumptionsE30 + assumptionsE31 + assumptionsD46 === 0
    ) {
      value = 0;
    }

    if (includeInvestorPayoutInHistorical === false) {
      value = 0;
    }

    return {
      type: "dollar",
      isLocked: true,
      isNegative: true,
      value: -Number(value),
    };
  });

export const totalEquityInvestorDistributionsMolecule =
  createComputedCellMoleculeFromEpochs((get, i) => {
    const preferredDividends = get(
      get(totalEquityPreferredDividendsMolecule)[i],
    ).value as MathResult;

    const principalRepayments = get(
      get(totalEquityPrincipalRepaymentsMolecule)[i],
    ).value as MathResult;

    const profitDistributions = get(
      get(totalEquityProfitDistributionsMolecule)[i],
    ).value as MathResult;

    return {
      type: "dollar",
      isLocked: true,
      isNegative: true,
      value: solve<DollarAmount>(
        "preferredDividends + principalRepayments + profitDistributions",
        {
          preferredDividends: preferredDividends,
          principalRepayments: principalRepayments,
          profitDistributions: profitDistributions,
        },
      ),
    };
  });

export const useableFreeCashFlowMolecule = createComputedCellMoleculeFromEpochs(
  (get, i) => {
    const distributableFreeCashFlow = get(
      get(distributableFreeCashFlowMolecule)[i],
    ).value as MathResult;

    const totalEquityInvestorDistributions = get(
      get(totalEquityInvestorDistributionsMolecule)[i],
    ).value as MathResult;

    return {
      type: "dollar",
      isLocked: true,
      value: solve<DollarAmount>(
        "distributableFreeCashFlow - totalEquityInvestorDistributions",
        {
          distributableFreeCashFlow: distributableFreeCashFlow,
          totalEquityInvestorDistributions: totalEquityInvestorDistributions,
        },
      ),
    };
  },
);
